/* =========================================================
   SIZE GUARD v4 — Require explicit Size selection (Size-2 supported)
   Fixes:
   - Works even when inputs DON'T have form="..."
   - Tracks "touched" per product scope (product page / quick-add modal)
   - Clears auto-selected size (e.g., S) on color changes until user chooses size
   ========================================================= */

(() => {
  const ERROR_TEXT = "Please select a size before adding to cart.";

  const handleize = (str) =>
    String(str || "")
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

  // IMPORTANT: This matches "Size-2" because it starts with "size"
  const isSizeName = (name) => {
    const h = handleize(name);
    if (!h) return false;
    return h === "size" || h.startsWith("size-") || h.includes("-size-") || h.endsWith("-size");
  };

  const getOptionNameFromContainer = (container) => {
    const legend = container.querySelector("legend.form__label, legend");
    if (legend) {
      const clone = legend.cloneNode(true);
      clone.querySelectorAll("span, small, svg").forEach((n) => n.remove());
      return (clone.textContent || "").trim().replace(/:$/, "").trim();
    }
    const label = container.querySelector("label.form__label, label");
    if (label) return (label.textContent || "").trim();
    return "";
  };

  // Product "scope" = the area that contains one product’s picker + its add-to-cart form.
  const getScope = (el) =>
    el.closest("product-info, .quick-add-modal__content, .quick-add-modal, section") || document;

  const getVariantSelects = (scope) => scope.querySelector("variant-selects");

  const getAddToCartForm = (scope) =>
    scope.querySelector('form[action*="/cart/add"]') ||
    scope.querySelector('form[action="/cart/add"]');

  const getOptionContainers = (vs) =>
    Array.from(vs.querySelectorAll(".product-form__input"));

  const getSizeContainers = (vs) => {
    const containers = getOptionContainers(vs);
    const sizeContainers = [];

    for (const c of containers) {
      const name = getOptionNameFromContainer(c);
      const isSize = isSizeName(name);
      c.classList.toggle("size-guard-size", isSize);
      if (isSize) sizeContainers.push(c);
    }
    return sizeContainers;
  };

  const countChoices = (container) => {
    const radios = container.querySelectorAll('input[type="radio"]');
    if (radios.length) return radios.length;
    const sel = container.querySelector("select");
    if (sel) return Array.from(sel.options).filter((o) => o.value !== "").length;
    return 0;
  };

  const setPrompt = (container, text) => {
    const span = container.querySelector("[data-selected-value]");
    if (span) span.textContent = text;
  };

  const clearSizeSelection = (container) => {
    container.querySelectorAll('input[type="radio"]').forEach((r) => {
      r.checked = false;
      r.removeAttribute("checked");
    });

    const sel = container.querySelector("select");
    if (sel) {
      // ensure placeholder exists
      const hasBlank = Array.from(sel.options).some((o) => o.value === "");
      if (!hasBlank) {
        const opt = document.createElement("option");
        opt.value = "";
        opt.textContent = "Select size";
        sel.insertBefore(opt, sel.firstChild);
      }
      sel.value = "";
    }

    setPrompt(container, "Select size");
  };

  const isSizeChosen = (container) => {
    if (container.querySelector('input[type="radio"]:checked')) return true;
    const sel = container.querySelector("select");
    return sel ? sel.value !== "" : false;
  };

  const hideError = (scope) => {
    const wrap = scope.querySelector(".product-form__error-message-wrapper");
    if (wrap) {
      wrap.hidden = true;
      wrap.setAttribute("hidden", "hidden");
    }
    scope.querySelectorAll(".size-guard-inline-error").forEach((n) => n.remove());
  };

  const showError = (form, scope) => {
    const pf = form.closest("product-form") || form.parentElement;
    const wrap = pf?.querySelector?.(".product-form__error-message-wrapper");
    const msg = pf?.querySelector?.(".product-form__error-message");

    if (wrap && msg) {
      msg.textContent = ERROR_TEXT;
      wrap.hidden = false;
      wrap.removeAttribute("hidden");
    } else {
      let box = form.querySelector(".size-guard-inline-error");
      if (!box) {
        box = document.createElement("div");
        box.className = "size-guard-inline-error";
        box.setAttribute("role", "alert");
        form.prepend(box);
      }
      box.textContent = ERROR_TEXT;
    }

    const vs = getVariantSelects(scope);
    const sizeContainers = vs ? getSizeContainers(vs) : [];
    const target = sizeContainers[0];
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "center" });
      (target.querySelector("input, select, button, label"))?.focus?.({ preventScroll: true });
    }
  };

  // State per scope (so it survives picker re-renders inside the same product area)
  const touchedByScope = new WeakMap(); // scope -> boolean
  const rafByScope = new WeakMap();     // scope -> raf id

  const lock = (vs) => (vs.dataset.sizeGuard = "lock");
  const unlock = (vs) => delete vs.dataset.sizeGuard;

  const stopClearLoop = (scope) => {
    const rid = rafByScope.get(scope);
    if (rid) cancelAnimationFrame(rid);
    rafByScope.delete(scope);
  };

  const clearAutoSizeForFrames = (scope, frames = 60) => {
    if (touchedByScope.get(scope)) return;

    stopClearLoop(scope);

    const tick = (left) => {
      if (touchedByScope.get(scope)) {
        stopClearLoop(scope);
        return;
      }

      const vs = getVariantSelects(scope);
      if (!vs) return;

      const sizeContainers = getSizeContainers(vs);
      const hasRealChoice = sizeContainers.some((c) => countChoices(c) > 1);
      if (hasRealChoice) sizeContainers.forEach(clearSizeSelection);

      if (left > 0) {
        const rid = requestAnimationFrame(() => tick(left - 1));
        rafByScope.set(scope, rid);
      } else {
        rafByScope.delete(scope);
      }
    };

    const rid = requestAnimationFrame(() => tick(frames));
    rafByScope.set(scope, rid);
  };

  const initScope = (scope) => {
    const vs = getVariantSelects(scope);
    if (!vs) return;

    const sizeContainers = getSizeContainers(vs);
    if (!sizeContainers.length) {
      unlock(vs);
      return;
    }

    const hasRealChoice = sizeContainers.some((c) => countChoices(c) > 1);
    if (!hasRealChoice) {
      unlock(vs);
      return;
    }

    if (touchedByScope.get(scope)) {
      unlock(vs);
      return;
    }

    lock(vs);
    sizeContainers.forEach((c) => setPrompt(c, "Select size"));
    clearAutoSizeForFrames(scope, 120);
  };

  const initAll = () => {
    document.querySelectorAll("product-info, .quick-add-modal__content, section").forEach(initScope);
  };

  document.addEventListener("DOMContentLoaded", initAll);
  window.addEventListener("pageshow", initAll);

  // Re-init for injected quick add modal content
  const mo = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (!(node instanceof HTMLElement)) continue;
        const scope =
          node.matches?.(".quick-add-modal__content") ? node :
          node.querySelector?.(".quick-add-modal__content") || null;
        if (scope) initScope(scope);
      }
    }
  });
  mo.observe(document.documentElement, { childList: true, subtree: true });

  // If user explicitly interacts with SIZE: unlock + mark touched
  document.addEventListener(
    "click",
    (e) => {
      if (!e.isTrusted) return;
      const t = e.target;
      if (!(t instanceof HTMLElement)) return;

      const scope = getScope(t);
      const vs = getVariantSelects(scope);
      if (!vs) return;

      const sizeContainer = t.closest(".size-guard-size");
      if (!sizeContainer) return;

      // Only count clicks that can be a deliberate selection
      if (!t.closest('label, input[type="radio"]')) return;

      touchedByScope.set(scope, true);
      stopClearLoop(scope);
      unlock(vs);
      hideError(scope);
    },
    true
  );

  document.addEventListener(
    "change",
    (e) => {
      const t = e.target;
      if (!(t instanceof HTMLElement)) return;

      const scope = getScope(t);
      const vs = getVariantSelects(scope);
      if (!vs) return;

      const container = t.closest(".product-form__input");
      if (!container) return;

      const name = getOptionNameFromContainer(container);
      const isSize = isSizeName(name);

      if (isSize && e.isTrusted) {
        touchedByScope.set(scope, true);
        stopClearLoop(scope);
        unlock(vs);
        hideError(scope);
        return;
      }

      // If they change color/anything else before choosing size:
      if (!touchedByScope.get(scope) && !isSize) {
        lock(vs);
        const sizeContainers = getSizeContainers(vs);
        sizeContainers.forEach((c) => setPrompt(c, "Select size"));
        clearAutoSizeForFrames(scope, 80);
      }
    },
    true
  );

  // Block add-to-cart until size was explicitly chosen in that scope
  document.addEventListener(
    "submit",
    (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;

      const action = (form.getAttribute("action") || "").toLowerCase();
      if (!action.includes("/cart/add")) return;

      const scope = getScope(form);
      const vs = getVariantSelects(scope);
      if (!vs) return;

      const sizeContainers = getSizeContainers(vs);
      if (!sizeContainers.length) return;

      const hasRealChoice = sizeContainers.some((c) => countChoices(c) > 1);
      if (!hasRealChoice) return;

      // Must have explicitly selected a size
      if (!touchedByScope.get(scope)) {
        e.preventDefault();
        e.stopImmediatePropagation();
        lock(vs);
        sizeContainers.forEach((c) => {
          setPrompt(c, "Select size");
          clearSizeSelection(c);
        });
        showError(form, scope);
        return;
      }

      // Safety: if unlocked but no size chosen (edge case), still block
      const chosen = sizeContainers.some(isSizeChosen);
      if (!chosen) {
        e.preventDefault();
        e.stopImmediatePropagation();
        lock(vs);
        sizeContainers.forEach((c) => {
          setPrompt(c, "Select size");
          clearSizeSelection(c);
        });
        showError(form, scope);
      }
    },
    true
  );
})();
